import React, { useState, useEffect } from "react";
import { addDoc, collection } from "firebase/firestore";
import { db } from "../firebase";
import { useLocation } from "react-router-dom";
import emailjs from "@emailjs/browser";
import "./Reservation.css";
import FaqSection from "../components/FaqSectionL";
import MenuNav from "../components/Menunav";
import MobileMenu from "../components/MobileMenu";
import RentalCarousel from "../components/RentalCarousel";

// Liste de pays africains avec indicatif et longueur du numéro sans indicatif
const africanCountries = [
  { name: "Togo", code: "+228", length: 8 },
  { name: "Bénin", code: "+229", length: 8 },
  { name: "Côte d'Ivoire", code: "+225", length: 10 },
  { name: "Sénégal", code: "+221", length: 9 },
  { name: "Burkina Faso", code: "+226", length: 8 },
  { name: "Mali", code: "+223", length: 8 },
  { name: "Niger", code: "+227", length: 8 },
  { name: "Cameroun", code: "+237", length: 9 },
  { name: "RD Congo", code: "+243", length: 9 },
  { name: "Gabon", code: "+241", length: 7 },
  { name: "Guinée", code: "+224", length: 9 },
  { name: "Tchad", code: "+235", length: 8 }
];

export default function Reservation() {
  const location = useLocation();
  const vehicule = location.state?.vehicule || null;
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const [formData, setFormData] = useState({
    nom: "",
    email: "",
    pays: "+228", // par défaut Togo
    numero: "",
    dateDebut: "",
    dateFin: "",
    vehicule: vehicule ? `${vehicule.marque} ${vehicule.modele}` : "",
    vehicleId: vehicule?.id || ""
  });

  useEffect(() => {
    if (vehicule) {
      setFormData((prev) => ({
        ...prev,
        vehicule: `${vehicule.marque} ${vehicule.modele}`,
        vehicleId: vehicule.id,
        imageUrl: vehicule?.imageUrls?.[0] || null
      }));
    }
  }, [vehicule]);

  const getSelectedCountry = () => {
    return africanCountries.find((c) => c.code === formData.pays);
  };

  const isValidEmail = (email) => {
    const regex = /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/;
    return regex.test(email);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const { nom, pays, numero, email, dateDebut, dateFin } = formData;
    const selectedCountry = getSelectedCountry();

    // Validation des champs
    if (!nom || !email || !numero || !dateDebut || !dateFin) {
      return setError("Tous les champs sont obligatoires.");
    }

    if (!isValidEmail(email)) {
      return setError("Adresse email invalide.");
    }

    if (numero.length !== selectedCountry.length || !/^\d+$/.test(numero)) {
      return setError(`Le numéro doit contenir ${selectedCountry.length} chiffres pour ${selectedCountry.name}.`);
    }

    if (new Date(dateDebut) > new Date(dateFin)) {
      return setError("La date de fin doit être postérieure à la date de début.");
    }

    const fullPhoneNumber = `${pays}${numero}`;

    try {
      const docRef = await addDoc(collection(db, "reservations"), {
        ...formData,
        telephone: fullPhoneNumber,
        statut: "en attente de validation",
        createdAt: new Date()
      });

      await emailjs.send(
        "service_f6qpmie",
        "template_y8h0myv",
        {
          nom,
          telephone: fullPhoneNumber,
          email,
          vehicule: formData.vehicule,
          dateDebut: new Date(dateDebut).toLocaleDateString(),
          dateFin: new Date(dateFin).toLocaleDateString(),
          lien: `https://thebridgeofworld.com/reservation/details/${docRef.id}`,
          message: `
📋 NOUVELLE DEMANDE DE RÉSERVATION

👤 Nom : ${nom}
📞 Téléphone : ${fullPhoneNumber}
✉️ Email : ${email}

🚗 Véhicule : ${formData.vehicule}
📅 Du ${new Date(dateDebut).toLocaleDateString()} au ${new Date(dateFin).toLocaleDateString()}

🔗 Voir la demande : https://thebridgeofworld.com/reservation/details/${docRef.id}
💬 WhatsApp : https://wa.me/${fullPhoneNumber.replace(/\D/g, '')}
✉️ Email : mailto:${email}
          `.trim()
        },
        "ue1japEYODKjLR_iZ"
      );

      setSubmitted(true);
    } catch (err) {
      setError("Erreur lors de l'envoi de la réservation.");
      console.error(err);
    }
  };

  return (
    <>
      <MenuNav />
      <MobileMenu />
      <RentalCarousel />
      <div className="container py-5">
        <div className="page-title mb-4">
          <h2 className="fw-bold">🚗 Réservez votre voiture</h2>
          <p className="text-muted">Complétez les informations ci-dessous</p>
        </div>
        
  {vehicule?.imageUrls?.length > 0 && (
  <div className="vehicle-gallery mb-5">
    <h5 className="fw-bold text-center mb-3">Aperçu du véhicule</h5>
    <div id="vehicleCarousel" className="carousel slide" data-bs-ride="carousel">
      <div className="carousel-inner rounded shadow" style={{height: "550px"}}>
        {vehicule.imageUrls.map((url, index) => (
          <div key={index} className={`carousel-item ${index === 0 ? "active" : ""}`}>
            <img
              src={url}
              className="d-block w-100"
              alt={`Véhicule image ${index + 1}`}
              style={{ height: "550px", objectFit: "cover" }}
            />
          </div>
        ))}
      </div>

      {/* Flèches de navigation (optionnelles sur mobile) */}
      {vehicule.imageUrls.length > 1 && (
        <>
          <button className="carousel-control-prev" type="button" data-bs-target="#vehicleCarousel" data-bs-slide="prev">
            <span className="carousel-control-prev-icon" aria-hidden="true" />
            <span className="visually-hidden">Précédent</span>
          </button>
          <button className="carousel-control-next" type="button" data-bs-target="#vehicleCarousel" data-bs-slide="next">
            <span className="carousel-control-next-icon" aria-hidden="true" />
            <span className="visually-hidden">Suivant</span>
          </button>
        </>
      )}
    </div>
  </div>
)}


        {error && <div className="alert alert-danger">{error}</div>}

        {submitted ? (
          <div className="alert alert-success text-center p-5">
            <h4>✅ Réservation envoyée !</h4>
            <p>Nous vous contacterons sous peu.</p>
          </div>
        ) : (
          
          <form onSubmit={handleSubmit} className="reservation-form mx-auto" style={{ maxWidth: "600px" }}>
            <div className="mb-3">
              <label className="form-label">Nom complet</label>
              <input type="text" name="nom" className="form-control" value={formData.nom} onChange={handleChange} required />
            </div>

            <div className="mb-3">
              <label className="form-label">Pays</label>
              <select name="pays" className="form-select" value={formData.pays} onChange={handleChange} required>
                {africanCountries.map((country) => (
                  <option key={country.code} value={country.code}>
                    {country.name} ({country.code})
                  </option>
                ))}
              </select>
            </div>

            <div className="mb-3">
              <label className="form-label">Numéro de téléphone (sans indicatif)</label>
              <input
                type="tel"
                name="numero"
                className="form-control"
                placeholder="Ex: 90112233"
                value={formData.numero}
                onChange={handleChange}
                required
              />
              <div className="form-text">Format requis : {getSelectedCountry().length} chiffres pour {getSelectedCountry().name}</div>
            </div>

            <div className="mb-3">
              <label className="form-label">Email</label>
              <input type="email" name="email" className="form-control" value={formData.email} onChange={handleChange} required />
            </div>

            <div className="mb-3">
              <label className="form-label">Véhicule</label>
              <input type="text" className="form-control" value={formData.vehicule} readOnly />
            </div>

            <div className="row mb-3">
              <div className="col">
                <label className="form-label">Date début</label>
                <input type="date" name="dateDebut" className="form-control" value={formData.dateDebut} onChange={handleChange} required />
              </div>
              <div className="col">
                <label className="form-label">Date fin</label>
                <input type="date" name="dateFin" className="form-control" value={formData.dateFin} onChange={handleChange} required />
              </div>
            </div>

            <button className="btn btn-primary w-100 rounded-pill">📩 Envoyer la demande</button>
          </form>
        )}
      </div>
      <FaqSection />
    </>
  );
}
